import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../utils/cn';
import { XIcon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';

export interface DrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Function to call when the drawer should be closed */
  onClose: () => void;
  /** Drawer placement */
  placement?: 'left' | 'right' | 'top' | 'bottom';
  /** Drawer size */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Whether clicking the overlay should close the drawer */
  closeOnOverlayClick?: boolean;
  /** Whether pressing escape should close the drawer */
  closeOnEscape?: boolean;
  /** Drawer content */
  children: React.ReactNode;
  /** Additional class names for the drawer content */
  className?: string;
}

const drawerSizes = {
  sm: '320px',
  md: '384px',
  lg: '448px',
  xl: '512px',
  full: '100%',
};

const drawerPlacements = {
  left: {
    container: 'justify-start',
    content: 'h-full',
    transform: {
      closed: '-translate-x-full',
      open: 'translate-x-0',
    },
  },
  right: {
    container: 'justify-end',
    content: 'h-full',
    transform: {
      closed: 'translate-x-full',
      open: 'translate-x-0',
    },
  },
  top: {
    container: 'items-start',
    content: 'w-full',
    transform: {
      closed: '-translate-y-full',
      open: 'translate-y-0',
    },
  },
  bottom: {
    container: 'items-end',
    content: 'w-full',
    transform: {
      closed: 'translate-y-full',
      open: 'translate-y-0',
    },
  },
};

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  placement = 'left',
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  children,
  className,
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const config = drawerPlacements[placement];

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, closeOnEscape, onClose]);

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const sizeStyle = placement === 'left' || placement === 'right' 
    ? { width: drawerSizes[size] }
    : { height: drawerSizes[size] };

  return createPortal(
    <div
      className={cn('fixed inset-0 z-50 flex', config.container)}
      onClick={handleOverlayClick}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-neutral-950/55 backdrop-blur-sm" />
      
      {/* Drawer */}
      <div
        ref={drawerRef}
        className={cn(
          'relative bg-neutral-50 dark:bg-neutral-800 shadow-2xl',
          'border border-neutral-200 dark:border-neutral-700',
          'transition-transform duration-300 ease-luxury',
          config.content,
          isOpen ? config.transform.open : config.transform.closed,
          className
        )}
        style={sizeStyle}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export const DrawerHeader: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
  showCloseButton?: boolean;
  onClose?: () => void;
}> = ({
  children,
  className,
  showCloseButton = true,
  onClose,
}) => (
  <div className={cn('flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700', className)}>
    <div className="flex-1">{children}</div>
    {showCloseButton && onClose && (
      <IconButton
        variant="ghost"
        size="sm"
        icon={<XIcon size="md" />}
        onClick={onClose}
        aria-label="Close drawer"
        className="ml-2 text-current hover:bg-neutral-50/20 dark:hover:bg-neutral-50/20"
      />
    )}
  </div>
);

export const DrawerBody: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn('flex-1 overflow-y-auto p-6', className)}>
    {children}
  </div>
);

export const DrawerFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn('p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-3', className)}>
    {children}
  </div>
);
